import { useContext } from 'react'

import { unpick, path, mapObj } from '../obj'
import { contentTypeId } from '../contentful'
import { whenNotNil } from '../nil'

import {
  EVCComponentContext,
  EVCDataContext,
  EVCOptionsContext,
  EVTrackingContext,
  SlimlineContext,
} from './provider'
import { useExperiences as useExperiencesChansey } from '@uswitch/ev-utils/lib/experiments'
import { debugHydration, forceHydration } from '../helpers/env'

const MODES = {
  TEST: 'test',
  DEBUG: 'debug',
}

export const Slimcludes = []

export const useBrand = () => useMeta(['brand'])?.toLowerCase()

export const useExperiences = useExperiencesChansey

export const useTracking = () => useContext(EVTrackingContext).track
export const useTrackAll = () => useContext(EVTrackingContext).client.trackAll

export const useDataSelector = (s = i => i, defaultValue) => {
  const data = useContext(EVCDataContext)
  if (typeof s === 'function') whenNotNil(s(data), defaultValue)
  if (Array.isArray(s)) return path(s, data, defaultValue)
  return data
}

export const useDoHydrate = () => useDataSelector(['toHydrate'], [])
export const useForceHydration = () => useDataSelector(['forceHydration'], forceHydration)
export const useDebugHydration = () => useDataSelector(['debugHydration'], debugHydration)
export const useDoHydrateComponent = id => {
  const doHydrateIds = useDoHydrate()
  const forceHydration = useForceHydration()
  const doHydrate = forceHydration || doHydrateIds.includes(id)
  return doHydrate
}

export const useDontLazyload = () => useDataSelector(['dontLazyLoad'], false)

export const useIncludes = _ => useDataSelector(['includes'])

export const useTestMode = () => {
  const mode = useDataSelector(['mode'])
  return [mode, MODES]
}

export const useOptions = () => {
  const Options = useContext(EVCOptionsContext)
  return Options
}

export const getEntity = (props, includes = {}) => {
  if (Object.keys(includes).length === 0) {
    console.log('empty includes')
  }
  const id = path(['id'], props) || path(['sys', 'id'], props)
  const entity = path([id], includes)

  if (id && !entity) {
    console.log('No entity in getEntity hook', { props, id, entity })
    return { err: `ENOENTITY: unable to find entity for id ${id} in getEntity` }
  }

  return entity ? { ...props, ...entity.fields, sys: entity.sys } : {}
}

export const useEntity = props => getEntity(props, useIncludes())

export const useEntities = entries => {
  return entries.map(useEntity)
}

export const usePalette = palette => {
  const Slimline = useContext(SlimlineContext)

  const includes = useIncludes()
  const styles = unpick(['name', 'sys'], useEntity(palette))

  return mapObj(color => {
    const id = path(['sys', 'id'], color)
    Slimline.push(id)

    const hexcode = path([id, 'fields', 'hexcode'], includes)
    return `#${hexcode}`
  }, styles)
}

const firstValidFlag = (map, experiences = {}, flags = {}) => {
  return Object.entries(flags).find(([flag, variant]) => {
    if (!Object.prototype.hasOwnProperty.call(experiences, flag)) {
      return false
    }

    if (typeof experiences[flag].value !== 'boolean') {
      console.error(
        `EINVALIDCONFIG: invalid flag ${flag} used in meta when rendering - not boolean`,
      )
      return false
    }

    return experiences[flag].value === true && map[variant]
  })
}

export const useComponent = (props = {}) => {
  const flagState = useMeta('releaseFlags')
  const experiences = useExperiences()

  // TODO: fix that below
  let cMap = useContext(EVCComponentContext)
  if (cMap.EVCoreDefaultComponents) {
    cMap = cMap.EVCoreDefaultComponents
  }

  const componentId = props.tag || contentTypeId(props)
  const component = cMap[componentId]
  if (!component)
    return [`ENOCOMPONENT: unable to find component ID in ${props?.sys?.contentTypeId}`]

  if (component && component.variations) {
    const componentMap = component.variations

    if (!componentMap.default) {
      return [`EINVALIDCONFIG: invalid config for ${componentId} - missing default map`]
    }

    const [, variant] = firstValidFlag(componentMap, experiences, flagState) || []
    const { variations, wrapperConfig, features, ...config } = component
    return variant
      ? [null, componentMap[variant], component.wrapperConfig, component.features ?? {}, config]
      : [null, componentMap.default, component.wrapperConfig, component.features ?? {}, config]
  }

  return [
    `ENOCOMPONENT: unable to find component for ${[props.tag, contentTypeId(props)]
      .filter(i => i)
      .join('||')}`,
  ]
}

export const useComponentMeta = (component, variant, defaultValue = {}) => {
  const path = variant ? [component, variant] : [component]
  return useMeta(path, defaultValue)
}

export const useMeta = (path, defaultValue) => {
  return useDataSelector(['meta'].concat(path), defaultValue)
}
